import { Component, OnInit } from "@angular/core";
import { APIService } from "../API.service";
import { UserService } from "../users/user.service";
import { MatSnackBar, MatDialog } from "@angular/material";

@Component({
  selector: "app-confirm-delete-poitype-dialog",
  template: `
    <h2 mat-dialog-title>Elimina POI Type</h2>
    <mat-dialog-content>
      Attenzione: verificare che nessun POI utilizzi questo tipo prima di
      eliminarlo. Procedere?
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Annulla</button>
      <button mat-button color="warn" [mat-dialog-close]="true" cdkFocusInitial>
        Sì, elimina
      </button>
    </mat-dialog-actions>
  `,
})
export class ConfirmDeletePoiTypeDialogComponent {}

type FieldType = "text" | "longtext" | "select" | "radio" | "fileselector";
type FieldOption = { label: string; value: any };
interface Field {
  id: string;
  type: FieldType;
  label: string;
  order: number;
  value: string;
  options: FieldOption[];
}
interface Section {
  key: string;
  name: string;
  order: number;
  items: Field[];
}

@Component({
  selector: "app-poitype-builder",
  templateUrl: "./poitype-builder.component.html",
  styleUrls: ["./poitype-builder.component.css"],
})
export class PoitypeBuilderComponent implements OnInit {
  poiTypeName = "";
  poiTypes: {
    id?: string;
    name: string;
    sections: Section[];
  }[] = [];
  saving = false;
  savingId: string | null = null;
  deletingId: string | null = null;

  fields: Field[] = [
    {
      id: "",
      type: "text",
      label: "",
      order: 0,
      value: "",
      options: [],
    },
  ];

  fieldTypes = [
    { value: "text", label: "Text" },
    { value: "longtext", label: "Long text" },
    { value: "select", label: "Select" },
    { value: "radio", label: "Radio" },
    { value: "fileselector", label: "File selector" },
  ];

  constructor(
    private api: APIService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.fields[0].id = this.createFieldId();
    this.userService.currentuserAvailable.subscribe((data) => {
      if (data) {
        this.loadPoiTypes();
      }
    });
  }

  // Load the poi types
  private loadPoiTypes() {
    const user = this.userService.currentUser;
    const org = user && user["organizationObject"];

    if (!org || !org.poiTypes || org.poiTypes.length === 0) {
      this.poiTypes = [];
      return;
    }

    Promise.all(
      org.poiTypes.map((id: string) =>
        this.api.GetPoiType(id).catch((err: any) => {
          // Amplify sometimes rejects with the response object when there are partial GraphQL errors
          if (err && err.data && err.data.getPoiType) {
            return err.data.getPoiType;
          }
          console.error("Error loading PoiType", id, err);
          return null;
        }),
      ),
    )
      .then((poiTypes) => {
        const valid = poiTypes.filter((pt) => !!pt);
        this.poiTypes = valid.map((poiType: any) =>
          this.mapPoiTypeForFrontend(poiType),
        );
        console.log(this.poiTypes);
      })
      .catch((err) => {
        console.error("Error in loadPoiTypes:", err);
      });
  }

  requestDeletePoiType(id: string) {
    const ref = this.dialog.open(ConfirmDeletePoiTypeDialogComponent, {
      width: "400px",
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) this.deletePoiType(id);
    });
  }

  // Check if a poi type is used by any poi
  // If is it, call the deletePoiTypeConfirmed function to delete the poi type
  deletePoiType(id: string) {
    if (this.deletingId) return;

    this.api
      .GetPoiType(id)
      .then((poiType: any) => {
        if (!poiType) {
          this.snackBar.open("PoiType non trovato", "OK", { duration: 3000 });
          return;
        }

        const items =
          poiType.poi && poiType.poi.items ? poiType.poi.items : [];

        if (items.length > 0 || poiType.poi.nextToken) {
          this.snackBar.open(
            `Il PoiType è in uso da ${items.length}${poiType.poi.nextToken ? "+" : ""} POI e non può essere eliminato`,
            "OK",
            { duration: 5000 },
          );
          return;
        }

        return this.deletePoiTypeConfirmed(id);
      })
      .catch((err: any) => {
        const poiType = err && err.data && err.data.getPoiType;
        if (poiType) {
          const items =
            poiType.poi && poiType.poi.items ? poiType.poi.items : [];
          if (items.length > 0 || poiType.poi.nextToken) {
            this.snackBar.open(
              `Il PoiType è in uso da ${items.length}${poiType.poi.nextToken ? "+" : ""} POI e non può essere eliminato`,
              "OK",
              { duration: 5000 },
            );
            return;
          }
          return this.deletePoiTypeConfirmed(id);
        }
        console.error("Error checking PoiType usage:", err);
        this.snackBar.open("Errore durante il controllo del PoiType — cancellazione annullata", "OK", {
          duration: 4000,
        });
      });
  }

  // Delete a poi type confirmed
  private deletePoiTypeConfirmed(id: string) {
    const user = this.userService.currentUser;
    const org = user && user["organizationObject"];

    this.deletingId = id;

    if (!org || !org.id) {
      this.snackBar.open("Organizzazione non trovata", "OK", {
        duration: 3000,
      });
      return;
    }

    if (!user) {
      this.snackBar.open("Utente non trovato", "OK", {
        duration: 3000,
      });
      return;
    }

    return this.api
      .DeletePoiType({ id })
      .then(() => {
        const updatedPoiTypes = (org.poiTypes || []).filter(
          (ptId: string) => ptId !== id,
        );

        return this.api.UpdateOrganizations({
          id: org.id,
          poiTypes: updatedPoiTypes,
        });
      })
      .then(() => {
        if (org) {
          org.poiTypes = (org.poiTypes || []).filter(
            (ptId: string) => ptId !== id,
          );
        }

        this.poiTypes = this.poiTypes.filter((pt) => pt.id !== id);
        this.deletingId = null;
      })
      .catch((err) => {
        this.deletingId = null;
        // Amplify partial-error: UpdateOrganizations succeeded despite the error
        if (err && err.data && err.data.updateOrganizations) {
          if (org) {
            org.poiTypes = (org.poiTypes || []).filter(
              (ptId: string) => ptId !== id,
            );
          }
          this.poiTypes = this.poiTypes.filter((pt) => pt.id !== id);
          return;
        }
        console.error("Error in deletePoiType:", err);
        this.snackBar.open("Errore durante l'eliminazione", "OK", {
          duration: 3000,
        });
        this.loadPoiTypes();
      });
  }

  addField() {
    this.fields.push({
      id: this.createFieldId(),
      type: "text",
      label: "",
      order: 0,
      value: "",
      options: [],
    });
  }

  // Remove a field from the "new POI type" draft form
  removeSubField(i: number) {
    if (this.fields.length > 1) {
      this.fields.splice(i, 1);
    }
  }

  // When the field type is changed, add an option if the field is a select or radio
  fieldTypeChanged(field: Field) {
    if (
      (field.type === "select" || field.type === "radio") &&
      field.options.length === 0
    ) {
      this.addOption(field);
    }
    if (field.type !== "select" && field.type !== "radio") {
      field.options = [];
    }
  }

  addOption(field: Field) {
    field.options.push({ label: "", value: "" });
  }

  removeOption(field: Field, i: number) {
    if (field.options.length > 1) {
      field.options.splice(i, 1);
    }
  }

  savePoiType() {
    this.createPoiType();
  }

  // Update a poi type
  updatePoiType(poiType: { id: string; name: string; sections: Section[] }) {
    if (!poiType.name) {
      this.snackBar.open("Inserisci un nome per il PoiType", "OK", {
        duration: 3000,
      });
      return;
    }
    const structure: any = {};
    poiType.sections.forEach((section) => {
      const items: any = {};
      section.items.forEach((item, index) => {
        const key = this.resolveFieldId(item);
        const entry: any = {
          label: item.label,
          type: item.type,
          order: item.order,
          value: item.value,
        };
        if (item.type === "select" || item.type === "radio") {
          entry.options = item.options;
        }
        items[key] = entry;
      });
      structure[section.key] = {
        name: section.name,
        order: section.order,
        items,
      };
    });

    this.savingId = poiType.id;
    this.api
      .UpdatePoiType({
        id: poiType.id,
        name: poiType.name,
        structure: JSON.stringify(structure),
      })
      .then(() => {
        this.savingId = null;
        this.snackBar.open("Modifiche salvate!", "OK", { duration: 3000 });
      })
      .catch((err) => {
        this.savingId = null;
        console.error("Errore updatePoiType:", err);
        this.snackBar.open("Errore durante il salvataggio", "OK", {
          duration: 3000,
        });
      });
  }

  // Create a new poi type
  createPoiType() {
    if (!this.poiTypeName) {
      this.snackBar.open("Inserisci un nome per il PoiType", "OK", {
        duration: 3000,
      });
      return;
    }

    if (
      this.fields.some(
        (field) =>
          !field.label ||
          !field.value ||
          ((field.type === "select" || field.type === "radio") &&
            field.options.length === 0),
      )
    ) {
      this.snackBar.open("Compila tutti i campi", "OK", { duration: 3000 });
      return;
    }

    const user = this.userService.currentUser;
    const org = user && user["organizationObject"];

    if (!org || !org.id) {
      this.snackBar.open("Organizzazione non trovata", "OK", {
        duration: 3000,
      });
      return;
    }

    this.saving = true;
    const structure = this.buildStructure();
    let newPoiTypeId: string | null = null;

    this.api
      .CreatePoiType({
        name: this.poiTypeName,
        status: 1,
        structure: JSON.stringify(structure),
      })
      .then((result) => {
        newPoiTypeId = result.id;
        const currentPoiTypes: string[] = org.poiTypes ? [...org.poiTypes] : [];
        currentPoiTypes.push(newPoiTypeId);
        org.poiTypes = currentPoiTypes;
        return this.api.UpdateOrganizations({
          id: org.id,
          poiTypes: currentPoiTypes,
        });
      })
      .then(() => {
        this.saving = false;
        this.snackBar.open("PoiType salvato!", "OK", { duration: 3000 });
        this.resetNewPoiTypeForm();
        this.loadPoiTypes();
      })
      .catch((err) => {
        this.saving = false;
        if (
          newPoiTypeId &&
          err &&
          (err.id || (err.data && err.data.updateOrganizations))
        ) {
          // Amplify partial-error but data was saved
          this.snackBar.open("PoiType salvato!", "OK", { duration: 3000 });
          this.resetNewPoiTypeForm();
          this.loadPoiTypes();
        } else {
          if (newPoiTypeId) {
            // Rollback: PoiType created but org update failed
            this.api.DeletePoiType({ id: newPoiTypeId }).catch(() => {});
            org.poiTypes = (org.poiTypes || []).filter(
              (id: string) => id !== newPoiTypeId,
            );
          }
          console.error("Errore createPoiType:", err);
          this.snackBar.open("Errore durante il salvataggio", "OK", {
            duration: 3000,
          });
        }
      });
  }

  // Reset the new poi type form
  private resetNewPoiTypeForm() {
    this.poiTypeName = "";
    this.fields = [
      {
        id: this.createFieldId(),
        type: "text",
        label: "",
        order: 0,
        value: "",
        options: [],
      },
    ];
  }

  // Add a field to a poi type
  addFieldToPoiType(poiType: {
    id: string;
    name: string;
    sections: Section[];
  }) {
    poiType.sections.forEach((section) => {
      section.items.push({
        id: this.createFieldId(),
        type: "text",
        label: "",
        order: section.items.length,
        value: "",
        options: [],
      });
    });
  }

  // Add an option to a field
  addOptionToField(field: Field) {
    if (field.type === "select" || field.type === "radio") {
      field.options.push({ label: "", value: "" });
    }
  }

  // Remove a field from a poi type
  removeFieldFromPoiType(
    poiType: { id: string; name: string; sections: Section[] },
    fieldId: string,
  ) {
    poiType.sections.forEach((section) => {
      section.items = section.items.filter((item) => item.id !== fieldId);
    });
    this.updatePoiType(poiType);
  }

  // HELPERS
  private createFieldId() {
    return `fld_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private resolveFieldId(item: any, itemKey?: string): string {
    return (item && item.id) || itemKey || this.createFieldId();
  }

  // Map a poi type for the frontend
  private mapPoiTypeForFrontend(poiType: any) {
    let sections: Section[] = [];
    try {
      const parsedData = JSON.parse(poiType.structure);

      sections = Object.entries(parsedData)
        .map(([sectionKey, section]: [string, any]) => {
          const items: Field[] = Object.entries(section.items || {})
            .map(([itemKey, item]: [string, any]) => ({
              id: this.resolveFieldId(item, itemKey),
              type: item.type as FieldType,
              label: item.label,
              order: item.order,
              value: item.value,
              options:
                item.type === "select" || item.type === "radio"
                  ? item.options || []
                  : [],
            }))
            .sort((a, b) => a.order - b.order);

          return {
            key: sectionKey,
            name: section.name,
            order: section.order,
            items,
          };
        })
        .sort((a, b) => a.order - b.order);
    } catch (e) {
      console.error("Error parsing structure for PoiType", poiType.name, e);
    }

    return {
      id: poiType.id,
      name: poiType.name,
      sections,
    };
  }

  // Build the structure of a poi type
  private buildStructure() {
    const items = {};

    this.fields.forEach((field, index) => {
      const key = field.id || field.label.toLowerCase().replace(/\s+/g, "_");
      const item: any = {
        label: field.label,
        type: field.type,
        order: index,
        value: field.value,
      };
      if (field.type === "select" || field.type === "radio") {
        item.options = field.options;
      }
      items[key] = item;
    });

    return {
      default: {
        name: "Default",
        order: 100,
        items,
      },
    };
  }
}
